import { Alert, Form, Modal } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { setCommissionRule } from './agentApi'
import { CommissionFields } from './CommissionFields'
import {
  COMMISSION_MODAL_INITIAL,
  toCommissionDraft,
  useCommissionState,
  type CommissionFormValues,
} from './commissionForm'

/**
 * Komissiya qoidasi muharriri.
 *
 * <h2>⚠️ MAYDONLAR `CommissionFields` DA</h2>
 * Ular yaratish formasida ham kerak (egasi, 2026-08-20) va nusxalash
 * ikki muharrirni bir kunda ajratib yuborardi. Bu oyna faqat
 * SAQLASH mantiqini boshqaradi.
 *
 * <h2>⚠️ PAKET TANLAGICHI OLIB TASHLANDI (egasi qarori, 2026-08-19)</h2>
 * Komissiya faqat BALANS TO'LDIRISHDA hisoblanadi, to'ldirishda esa
 * paket tushunchasi umuman YO'Q — ya'ni «qaysi paket uchun» degan
 * savolning javobi ham yo'q edi. Server endi `planId` bilan kelgan
 * qoidani 400 bilan rad etadi (SHARTNOMA §1), panel esa uni umuman
 * YUBORMAYDI.
 *
 * Jadvaldagi «Paket» ustuni QOLDI — eski qatorlarda `planId` bor va
 * uni ekrandan yo'qotish tarixni o'qib bo'lmaydigan qilardi.
 */
export function CommissionRuleModal({
  open,
  agentId,
  currency,
  perTxnLimitMinor,
  onClose,
}: {
  open: boolean
  agentId: string
  currency: string
  /** `null` — limit qo'yilmagan (cheksiz); o'lik pog'ona ogohlantirishi uchun */
  perTxnLimitMinor: number | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<CommissionFormValues>()

  /*
    ⚠️ TEKSHIRUV YOZAYOTGANDA ishlaydi, yuborilgandan keyin emas
    (egasi qarori 2026-08-19). Qatorni ko'chirish ham darhol
    tekshiriladi: chegaralar qat'iy o'suvchi bo'lishi kerak va
    ko'chirish aynan shu shartni buzadigan amal.
  */
  const state = useCommissionState(form, perTxnLimitMinor)
  const problems = state.problems

  const save = useMutation({
    mutationFn: (values: CommissionFormValues) =>
      setCommissionRule(toCommissionDraft(agentId, values)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents.commissionRules(agentId) })
      /*
        ⚠️ OLDINDAN KO'RISH HAM BEKOR QILINADI. Usiz saqlashdan keyin
        ekranda ESKI qoidaning raqami turardi — ya'ni operator o'zi
        endigina o'zgartirgan foizni ko'rmasdi va o'zgarish
        yozilmagan deb o'ylardi.
      */
      void queryClient.invalidateQueries({
        queryKey: ['agents', 'commissionPreview', agentId],
      })
      form.resetFields()
      onClose()
    },
  })

  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      width={760}
      title={t('agents.commissionSet')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      /*
        ⚠️ TUGMA O'CHIQ BO'LSA SABABI EKRANDA TURADI (XSB-COM-001 dan
        olingan saboq): tepada ogohlantirish, zinapoya ostida esa
        QAYSI QATOR ekani. Ilgari sabab faqat pastda edi va oyna
        suriladigan bo'lgani uchun operator uchun ekran shunchaki
        «bosilmayapti» bo'lib ko'rinardi.
      */
      okButtonProps={{ disabled: problems.length > 0 }}
      destroyOnHidden
    >
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      {problems.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('agents.commissionSaveBlocked')}
          description={t('agents.commissionSaveBlockedHint')}
        />
      ) : null}

      {/* ⚠️ Paketga bog'liqlik olib tashlangani OSHKORA aytiladi:
          operator bu tanlagichni ilgari ko'rgan va uni «yo'qolib
          qoldi» deb o'ylashi mumkin edi. */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('agents.commissionTopUpOnly')}
        description={t('agents.commissionTopUpOnlyHint')}
      />

      <Form
        form={form}
        layout="vertical"
        preserve={false}
        disabled={save.isPending}
        initialValues={COMMISSION_MODAL_INITIAL}
      >
        <CommissionFields
          currency={currency}
          state={state}
          perTxnLimitMinor={perTxnLimitMinor}
        />
      </Form>
    </Modal>
  )
}
